/**
 * Stories implemented for this component
 */
/* eslint-disable local-rules/require-storybook-documentation */
import { FC } from 'react';
import dynamic from 'next/dynamic';
import { ZHLPersonalizedNextStepState } from 'src/services/graphql/generated/graphql';
import { ResourceCardPropsSuperSet } from './types';
import { FirstPartyVerifiedPreApprovalResourceCard } from './experiences/FirstPartyVerifiedPreApprovalResourceCard/FirstPartyVerifiedPreApprovalResourceCard';
const LoadingResourceCard = dynamic(() =>
    import(
        /* webpackChunkName: "LoadingResourceCard" */
        './experiences/LoadingResourceCard/LoadingResourceCard'
    ).then(mod => mod.LoadingResourceCard)
);
const NewApplicationResourceCard = dynamic(() =>
    import(
        /* webpackChunkName: "NewApplicationResourceCard" */
        './experiences/NewApplicationResourceCard/NewApplicationResourceCard'
    ).then(mod => mod.NewApplicationResourceCard)
);

const VerifiedPreApprovalResourceCard = dynamic(() =>
    import(
        /* webpackChunkName: "VerifiedPreApprovalResourceCard" */
        './experiences/VerifiedPreApprovalResourceCard/VerifiedPreApprovalResourceCard'
    ).then(mod => mod.VerifiedPreApprovalResourceCard)
);
const OutstandingTasksResourceCard = dynamic(() =>
    import(
        /* webpackChunkName: "OutstandingTasksResourceCard" */
        './experiences/OutstandingTasksResourceCard/OutstandingTasksResourceCard'
    ).then(mod => mod.OutstandingTasksResourceCard)
);
const WaitingToCloseResourceCard = dynamic(() =>
    import(
        /* webpackChunkName: "WaitingToCloseResourceCard" */
        './experiences/WaitingToCloseResourceCard/WaitingToCloseResourceCard'
    ).then(mod => mod.WaitingToCloseResourceCard)
);

export type ResourceCardModuleProps = {
    /**
     * Whether data is loading
     */
    isLoading: boolean;
    /**
     * What personalized next step graphic to show
     */
    status: ZHLPersonalizedNextStepState | null;
    /**
     * Callback to handle variants that have a CTA to start the application embedded in them.
     */
    onStartApplication: () => void;
    /**
     * Whether the current experience is a first party experience
     */
    isFirstPartyExperience: boolean;
} & ResourceCardPropsSuperSet;

/**
 * This microfronted module produces an informational card content that adjusts it's content to be applicable
 * to the current Personalized Next Step status the user is in. When applicable, it offers access to the
 * Verified Preapproval letter, or a CTA back to the appropriate questionnaire for the user.
 */
export const ResourceCard: FC<ResourceCardModuleProps> = ({
    isLoading,
    status,
    trackCloseButtonClick,
    trackOutsideModalClick,
    trackPrimaryCTAClick,
    trackModalPrimaryCTAClick,
    onStartApplication,
    documentURL,
    downloadURL,
    isFirstPartyExperience,
}) => {
    if (isLoading || !status) {
        return <LoadingResourceCard />;
    }

    switch (status) {
        case ZHLPersonalizedNextStepState.NEW_APPLICATION: {
            return (
                <NewApplicationResourceCard
                    onModalPrimaryCTAClick={onStartApplication}
                    trackModalPrimaryCTAClick={trackModalPrimaryCTAClick}
                    trackOutsideModalClick={trackOutsideModalClick}
                    trackCloseButtonClick={trackCloseButtonClick}
                    trackPrimaryCTAClick={trackPrimaryCTAClick}
                />
            );
        }
        case ZHLPersonalizedNextStepState.VERIFIED_PREAPPROVAL_NO_TRID_OUTSTANDING_TASKS:
        case ZHLPersonalizedNextStepState.VERIFIED_PREAPPROVAL: {
            return isFirstPartyExperience ? (
                <FirstPartyVerifiedPreApprovalResourceCard
                    onStartApplication={onStartApplication}
                    trackModalPrimaryCTAClick={trackModalPrimaryCTAClick}
                    trackOutsideModalClick={trackOutsideModalClick}
                    trackCloseButtonClick={trackCloseButtonClick}
                    trackPrimaryCTAClick={trackPrimaryCTAClick}
                />
            ) : (
                <VerifiedPreApprovalResourceCard
                    trackModalPrimaryCTAClick={trackModalPrimaryCTAClick}
                    trackOutsideModalClick={trackOutsideModalClick}
                    trackCloseButtonClick={trackCloseButtonClick}
                    trackPrimaryCTAClick={trackPrimaryCTAClick}
                    documentURL={documentURL}
                    downloadURL={downloadURL}
                />
            );
        }
        case ZHLPersonalizedNextStepState.OUTSTANDING_TASKS: {
            return (
                <OutstandingTasksResourceCard
                    trackOutsideModalClick={trackOutsideModalClick}
                    trackCloseButtonClick={trackCloseButtonClick}
                    trackPrimaryCTAClick={trackPrimaryCTAClick}
                />
            );
        }
        case ZHLPersonalizedNextStepState.WAITING_TO_CLOSE: {
            return (
                <WaitingToCloseResourceCard
                    trackOutsideModalClick={trackOutsideModalClick}
                    trackCloseButtonClick={trackCloseButtonClick}
                    trackPrimaryCTAClick={trackPrimaryCTAClick}
                />
            );
        }
        default: {
            return null;
        }
    }
};
